import React from 'react';
import { Icon, Header, Popup, Segment } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import './index.css';
import {
  SECONDARY_COLOR,
  ALARM_COLOR,
  ALARM_COLOR_INVERTED,
  POSITIVE_COLOR,
  POSITIVE_COLOR_INVERTED,
  BLACK_COLOR,
  WHITE_COLOR,
  ICON_COLOR,
  ICON_COLOR_INVERTED,
} from '../colors';
import { TYPE_POSITIVE, TYPE_NEGATIVE, TYPE_INFORMATION } from '../constants';
import { colorsModeUI } from '../modeUI';

const colorsTitle = {
  [TYPE_POSITIVE]: {
    backgroundColor: POSITIVE_COLOR,
    color: POSITIVE_COLOR_INVERTED,
  },
  [TYPE_NEGATIVE]: {
    backgroundColor: ALARM_COLOR,
    color: ALARM_COLOR_INVERTED,
  },
  [TYPE_INFORMATION]: { backgroundColor: SECONDARY_COLOR, color: BLACK_COLOR },
};

const colorsTitleInverted = {
  [TYPE_POSITIVE]: {
    backgroundColor: POSITIVE_COLOR,
    color: POSITIVE_COLOR_INVERTED,
  },
  [TYPE_NEGATIVE]: {
    backgroundColor: ALARM_COLOR,
    color: ALARM_COLOR_INVERTED,
  },
  [TYPE_INFORMATION]: { backgroundColor: BLACK_COLOR, color: SECONDARY_COLOR },
};

const colorsValue = {
  [TYPE_POSITIVE]: { backgroundColor: WHITE_COLOR, color: POSITIVE_COLOR },
  [TYPE_NEGATIVE]: { backgroundColor: WHITE_COLOR, color: ALARM_COLOR },
  [TYPE_INFORMATION]: { backgroundColor: WHITE_COLOR, color: BLACK_COLOR },
};

const colorsValueInverted = {
  [TYPE_POSITIVE]: { backgroundColor: POSITIVE_COLOR, color: WHITE_COLOR },
  [TYPE_NEGATIVE]: { backgroundColor: ALARM_COLOR, color: WHITE_COLOR },
  [TYPE_INFORMATION]: { backgroundColor: BLACK_COLOR, color: WHITE_COLOR },
};

/**
 * Single data
 * this class creates a single display component to show a title and a
 * single value.
 * Parameters (Props):
 * @param string title
 * @param string value = if the value has more as 33 characters, then it will be cut
 * @param string description
 * @param string unitValue
 * @param string type = [TYPE_POSITIVE, TYPE_NEGATIVE, TYPE_INFORMATION]
 * @param func onClick = On click handler for the whole surface (optional).
 * @param bool showIconUpdate: if this is true, then it will be shown a update icon
 * @param func onClickIconUpdate: this function will be called from the update icon
 * @param bool showIconInfo: if this is true, then the icon Info will be shown at the
 *              top left and by click a popup is shown
 * @param string information: this variable is shown inside the popup
 * @param bool inverted: if this value is true, then the type will be ignored and the
 *                       text will be shown in color white
 * @param array styles: array with styles. it will be added to the main container and
 *                      the header container
 * @param bool showTitleBackground: by true the title will hava a backgroundColor
 * @param string mode
 */
export default class SingleDataSetPoint extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.state = {
      clickable: !!props.onClick,
    };
  }

  onClick(event, data) {
    const { onClick } = this.props;
    if (onClick) {
      onClick(event, data);
    }
  }

  render() {
    const { clickable } = this.state;
    const {
      title,
      value,
      description,
      showIconUpdate,
      showIconInfo,
      onClickIconUpdate,
      information,
      inverted,
      styles,
      mode,
      unitValue,
      showTitleBackground,
    } = this.props;
    let { type } = this.props;
    if (type !== TYPE_POSITIVE && type !== TYPE_NEGATIVE) {
      type = TYPE_INFORMATION;
    }
    const containerProps = {};
    if (clickable) {
      containerProps.onClick = this.onClick;
    }

    let stylesTitle = colorsTitle;
    let stylesValue = colorsValue;
    const iconColor =
      inverted || (!inverted && type !== TYPE_INFORMATION)
        ? ICON_COLOR_INVERTED
        : ICON_COLOR;
    if (inverted) {
      stylesTitle = colorsTitleInverted;
      stylesValue = colorsValueInverted;
    }

    const styleContainer = {
      backgroundColor: stylesTitle[type].backgroundColor,
      color: stylesTitle[type].color,
    };
    const styleIconUpdate = {
      color: iconColor,
    };
    const styleIconInfo = {
      color: iconColor,
    };
    const styleTitleContainer = {
      backgroundColor: stylesTitle[type].backgroundColor,
    };
    const styleTitle = {
      color: stylesTitle[type].color,
    };
    if (showIconUpdate) {
      styleTitle.paddingRight = '2rem';
    }
    if (showIconInfo) {
      styleTitle.paddingLeft = '2rem';
    }

    const styleValueContainer = {
      backgroundColor: stylesValue[type].backgroundColor,
    };
    const styleValue = {
      color: stylesValue[type].color,
    };
    const styleDescription = {
      backgroundColor: stylesValue[type].backgroundColor,
    };

    const stylePopup = {};
    if (mode !== null) {
      const colors = colorsModeUI(mode);
      styleDescription.backgroundColor = 'inherit';
      styleDescription.color = 'inherit';

      styleContainer.backgroundColor = 'inherit';
      styleContainer.color = 'inherit';
      styleIconUpdate.color = colors.SINGLE_DATA_TITLE_COLOR;
      styleTitleContainer.backgroundColor = showTitleBackground
        ? colors.SINGLE_DATA_TITLE_BACKGROUND_COLOR
        : 'inherit';
      styleTitle.color = colors.SINGLE_DATA_TITLE_COLOR;
      styleValueContainer.backgroundColor = 'inherit';
      styleValue.color = colors.SINGLE_DATA_VALUE_COLOR;
      stylePopup.backgroundColor = colors.POPUP_MAIN_MENU_BACKGROUND;
      stylePopup.color = colors.POPUP_MAIN_MENU_COLOR;
      stylePopup.border = '0px';
      stylePopup.boxShadow = colors.POPUP_SHADOW;
    }
    return (
      <div>
        {showIconInfo && (
          <div
            className="single-data-setpoint-info-button"
            style={styleIconInfo}
          >
            <Popup
              content={information}
              style={stylePopup}
              basic
              on="hover"
              trigger={
                <Icon
                  link
                  name="question circle"
                  inverted={
                    inverted || (!inverted && type !== TYPE_INFORMATION)
                  }
                />
              }
            />
          </div>
        )}
        <div
          className={`single-data-setpoint${clickable ? ' clickable' : ''}`}
          style={{
            ...styleContainer,
            ...styles,
          }}
          {...containerProps}
        >
          {description && (
            <div className="description" style={styleDescription}>
              {description}
            </div>
          )}
          {typeof value === 'object' && (
            <div
              className="value-object"
              style={{ ...styleValueContainer, ...styles, ...styleValue }}
            >
              {value}
            </div>
          )}
          {typeof value !== 'object' && (
            <div
              className="value-unit"
              style={{ ...styleValueContainer, ...styles }}
            >
              <div className="value" style={styleValue}>
                {typeof value !== 'object' &&
                  (value.length > 33 ? `${value.substr(0, 32)}...` : value)}
              </div>
              <div className="unit" style={styleValue}>
                {unitValue}
              </div>
              {showIconUpdate && (
                <div
                  className="single-data-setpoint-update-button"
                  style={styleIconUpdate}
                >
                  <Icon
                    link
                    name="write"
                    onClick={onClickIconUpdate}
                    inverted={
                      inverted || (!inverted && type !== TYPE_INFORMATION)
                    }
                  />
                </div>
              )}
            </div>
          )}
          <Segment
            basic
            textAlign="center"
            className="title"
            style={{
              ...styleTitleContainer,
              ...styles,
            }}
          >
            <Header
              as="h4"
              textAlign="right"
              className="header-setpoint"
              style={{
                ...styleTitle,
                ...styles,
              }}
            >
              {title}
            </Header>
          </Segment>
        </div>
      </div>
    );
  }
}

SingleDataSetPoint.defaultProps = {
  type: TYPE_INFORMATION,
  description: null,
  unitValue: null,
  onClick: null,
  showIconUpdate: false,
  onClickIconUpdate: null,
  showIconInfo: false,
  information: null,
  inverted: false,
  styles: {},
  showTitleBackground: false,
  mode: null,
};

SingleDataSetPoint.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  description: PropTypes.string,
  unitValue: PropTypes.string,
  type: PropTypes.string,
  onClick: PropTypes.func,
  showIconUpdate: PropTypes.bool,
  onClickIconUpdate: PropTypes.func,
  showIconInfo: PropTypes.bool,
  information: PropTypes.string,
  inverted: PropTypes.bool,
  styles: PropTypes.objectOf(PropTypes.string),
  showTitleBackground: PropTypes.bool,
  mode: PropTypes.string,
};
